/* eslint-disable comma-dangle */
/* eslint-disable no-unused-vars */
import React, { useRef, useState } from 'react';
import styled, { css, withTheme } from 'styled-components';
import get from 'lodash/get';
import './styles.css';

// Three card sizes are defined in current designs
// S / M / L Card Sizes
export const Size = {
  S: 'Small',
  M: 'Medium',
  L: 'Large',
  B: 'Banner',
};

// Card behaviour is distinguished in three states
export const State = {
  INCOMPLETE: 'incomplete',
  ACTIVE: 'active',
  COMPLETED: 'completed',
};

const ProgressLabel = styled.p`
  font-size: ${(props) => (props.size === Size.S ? '14px' : '14px')};
  color: ${(props) =>
    props.isActive
      ? props?.activeBreadcrumbTheme?.progressLabelColor
      : props?.disabledBreadcrumbTheme?.progressLabelColor};
  padding-left: 20px;
  padding-bottom: 10px;
  @media only screen and (min-width: 1280px) and (max-width: 1366px) {
    font-size: 14px;
  }
  @media only screen and (min-width: 1024px) and (max-width: 1280px) {
    font-size: 14px;
  }
  @media only screen and (min-width: 900px) and (max-width: 1023px) {
    font-size: 14px;
  }
  @media (max-width: 899px) {
    margin: 0;
  }
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const cardStyles = css`
  position: relative;
  ${({ isActive, size }) =>
    (isActive || size === Size.B) &&
    `
    cursor: pointer;
  `};
  box-shadow: ${({ breadcrumb, size, activeBreadcrumbTheme }) =>
    breadcrumb?.state === 'active' &&
    size !== Size.B &&
    (activeBreadcrumbTheme.boxShadow || '0px 0px 15px 5px rgba(0, 0, 0, 0.2)')};
  ${({ isActive, size }) =>
    isActive &&
    size !== Size.L &&
    size !== Size.B &&
    `background: rgba(255, 255, 255, 1);
  `};
  ${({ isActive, size }) =>
    !isActive &&
    size !== Size.B &&
    `display: -ms-grid!important;
  `};
`;

const BreadCrumbCard = styled.div`
  ${cardStyles};
  ${(props) => props.breadcrumbTheme};
  border-radius: ${({ borderRadius }) => borderRadius};
  border: ${({ isActive, activeBreadcrumbTheme, disabledBreadcrumbTheme }) =>
    isActive ? activeBreadcrumbTheme.border : disabledBreadcrumbTheme.border};
  &:focus {
    box-shadow: none;
    outline: 4px solid #037bb5;
    outline-offset: 1px;
  }
  @media (forced-colors: active) {
    ${({ isActive }) =>
      isActive
        ? `
      background: transparent !important;
      color: WindowText !important;
      border: 2px solid WindowText !important;
    `
        : `
      border: 2px solid Highlight !important;
      background: transparent;
    `}
  }
  @media (forced-colors: active) {
    ${({ breadcrumb }) =>
      breadcrumb?.state === 'active' &&
      `background: transparent !important;
      color: WindowText !important;
      border: 2px solid HighlightText !important;`}
  }
`;

const LeftSection = styled.div`
  width: 70%;
  @media (forced-colors: active) {
    ${({ breadcrumbState }) =>
      breadcrumbState === 'incomplete' &&
      `opacity: 0.5;
    `}
  }
  @media only screen and (min-width: 481px) and (max-width: 899px) {
    font-size: ${(props) => props.size === Size.L && '30px'};
    margin: ${(props) => props.size === Size.L && '10px 0 10px 15px'};
    flex-direction: column;
    margin: auto 0 0 0;
  }
  @media only screen and (max-width: 480px) {
    font-size: ${(props) => props.size === Size.L && '30px'};
    margin: ${(props) => props.size === Size.L && '10px 0 10px 15px'};
    flex-direction: column;
    margin: auto 0 0 0;
  }
`;

const LabelWrapper = styled.div`
  padding: 42px;
  ${({ size }) =>
    size !== Size.L &&
    size !== Size.B &&
    `padding: 14px;
    padding-left: 20px;
    position: relative;
    display: flex;
    align-items: baseline;
  `}
  @media only screen and (min-width: 1281px) {
    padding: 18px 20px 20px 18px;
  }
  @media only screen and (min-width: 1024px) and (max-width: 1280px) {
    padding: 18px 20px 20px 18px;
  }
  @media only screen and (min-width: 900px) and (max-width: 1023px) {
    padding: 18px 20px 20px 18px;
  }
  @media only screen and (min-width: 481px) and (max-width: 899px) {
    padding: 10px 20px 0;
  }
  @media only screen and (max-width: 480px) {
    padding: 10px 20px 0;
  }
`;

const ActiveIndicator = styled.div`
  color: ${({ activeBreadcrumbTheme }) => activeBreadcrumbTheme.titleColor};
  margin-right: 15px;
  transform: skew(-20deg);
  &:before {
    content: ' ';
    border-left: ${({ activeBreadcrumbTheme }) => `3px solid ${activeBreadcrumbTheme.titleColor}`};
    height: 20px;
    position: absolute;
    top: -20px;
  }
`;

const Title = styled.p`
  display: flex;
  align-items: flex-start;
  max-width: 83%;
  && {
    font-weight: bold;
    font-size: ${(props) => (props.size === Size.M ? '24px' : '24px')};
    font-size: ${(props) => props.size === Size.L && '48px'};
    font-size: ${(props) => props.size === Size.B && '40px'};
    color: ${(props) =>
      props.isActive || props.size === Size.B
        ? props.activeBreadcrumbTheme.titleColor
        : props.disabledBreadcrumbTheme.titleColor};
    margin: 0 !important;
    line-height: 1.2;
    position: relative;
    ${({ size }) =>
      size !== Size.L &&
      `
        height: 45px;
        max-height: 45px;
      `}
    @media only screen and (min-width: 1281px) {
      max-width: 90%;
    }
    @media only screen and (min-width: 1024px) and (max-width: 1280px) {
      && {
        font-weight: bold;
        font-size: ${(props) => (props.size === Size.M ? '20px' : '20px')};
        font-size: ${(props) => props.size === Size.L && '48px'};
        font-size: ${(props) => props.size === Size.B && '40px'};
        color: ${(props) =>
          props.isActive
            ? props.activeBreadcrumbTheme.titleColor
            : props.disabledBreadcrumbTheme.titleColor};
        margin: 0 !important;
        line-height: 1.2;
        position: relative;
        ${({ size }) =>
          size !== Size.L &&
          `
            height: 45px;
            max-height: 45px;
          `}
      }
    }
    @media only screen and (min-width: 900px) and (max-width: 1024px) {
      && {
        font-weight: bold;
        font-size: ${(props) => (props.size === Size.M ? '18px' : '18px')};
        font-size: ${(props) => props.size === Size.L && '48px'};
        font-size: ${(props) => props.size === Size.B && '40px'};
        color: ${(props) =>
          props.isActive
            ? props.activeBreadcrumbTheme.titleColor
            : props.disabledBreadcrumbTheme.titleColor};
        margin: 0 !important;
        line-height: 1.2;
        position: relative;
        ${({ size }) =>
          size !== Size.L &&
          `height: 36px;
          max-height: 36px;
        `}
      }
    }
    @media only screen and (min-width: 481px) and (max-width: 899px) {
      font-size: ${(props) => props.size === Size.L && '30px'};
      font-size: ${(props) => props.size === Size.B && '24px'};
      font-size: 20px;
      max-width: 300px;
    }
    @media only screen and (max-width: 480px) {
      font-size: 18px;
      font-size: ${(props) => props.size === Size.L && '30px'};
      font-size: ${(props) => props.size === Size.B && '18px'};
      max-width: 180px;
      ${({ size }) =>
        size === Size.B &&
        `
          height: auto;
          max-height: unset;
        `}
    }
    @media only screen and (max-width: 320px) {
      font-size: 16px;
    }
  }
`;

const TitleWrapper = styled.span`
  ${({ size, wordCount }) =>
    size !== Size.L &&
    size !== Size.B &&
    `
      z-index: 1;
      width : 240px;

      @media (min-width: 1281px) {
        width : 220px;
        padding-right: 5px;
      }
      @media (min-width: 1025px) and (max-width: 1280px) {
        width : ${wordCount > 2 ? '240px' : '150px'};
      }
      @media (min-width: 900px) and (max-width : 1024px){
        width : 156px;
      }
      @media (min-width: 481px) and (max-width : 899px){
        width : 300px;
      }
      @media (max-width: 480px) {
        width : ${wordCount > 2 ? '180px' : '150px'};
      }
      @media (max-width: 320px) {
        width : ${wordCount > 2 ? '180px' : '120px'};
      }
    `}
`;

const Footer = styled.span`
  font-size: 16px;
  color: rgba(93, 93, 93, 1);
`;

const Line = styled.hr`
  border: 1px solid rgba(216, 216, 216, 1);
  width: 80px;
  margin: 10px 0;
`;

const DisabledDiv = styled.div`
  z-index: -1;
  cursor: ${(props) => (props.state === State.INCOMPLETE ? 'not-allowed' : 'default')};
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.background};
  position: absolute;
  left: 0;
  border-radius: ${({ borderRadius }) => borderRadius};
`;

const Image = styled.img`
  position: absolute;
  height: 100%;
  width: 50px;
  right: 10px;
  ${({ breadCrumbId }) =>
    breadCrumbId === 'c4ca854b-7324-416b-8798-01c0c542db9c' && 'width:38px;right:15px'};
  ${({ breadCrumbId }) => breadCrumbId === 'partainsuredinfobreadcrumb' && 'width:57px;right:7px'};
  max-height: 70px;
  margin: 23px 2px;
  z-index: 99; // to display "inactive" image as per the configured image.
  @media (max-width: 900px) {
    height: 70%;
    margin-top: 12px;
  }

  border-bottom-right-radius: ${({ borderRadius }) => borderRadius};
  opacity: ${({ isActive, activeBreadcrumbTheme, disabledBreadcrumbTheme }) =>
    isActive ? activeBreadcrumbTheme.imageOpacity : disabledBreadcrumbTheme.imageOpacity};
  display: ${({ showImage }) => !showImage && 'none'};
`;

const CardWrapper = styled.div`
  width: ${({ override }) => (override ? '100%' : '90%')};
  margin: ${({ override }) => (override ? '5px 0' : '0')};
  z-index: 95;
  @media (min-width: 900px) {
    // max-width: 320px;
    width: 90%;
    margin-top: 10px;
  }
`;

const LeftBorder = styled.div`
  height: auto;
  width: ${({ width }) => width || '8px'};
  background: ${({ colorCode }) => colorCode || 'transparent'};
  z-index: 99;
`;

const showSectionProgressLabel = (totalSections, currentSectionNumber) => {
  if (
    typeof totalSections !== 'number' ||
    typeof currentSectionNumber !== 'number' ||
    (typeof totalSections === 'number' && totalSections < 0) ||
    (typeof currentSectionNumber === 'number' && currentSectionNumber < 0)
  ) {
    return <p />;
  }
  return `${currentSectionNumber} of ${totalSections} groups completed`;
};

/*
  Scroll focussed breadcrumb into view
  recursively scroll based on breadcrumb's index
*/
const scrollBreadrumbIntoView = (el) => {
  const elRect = el.getBoundingClientRect();

  if (elRect.bottom > window.innerHeight) {
    window.scrollBy(0, 500);
    scrollBreadrumbIntoView(el);
  } else if (elRect.top < 140) {
    const parentCard = el?.parentElement;
    const bcumbParent = parentCard?.parentElement;
    if (bcumbParent && bcumbParent?.hasChildNodes()) {
      const bcumbChildren = bcumbParent?.children;
      const elIndex = [...bcumbChildren].findIndex((c) => c.id === parentCard.id);

      if (elIndex < 3) {
        el.scrollIntoView();
        window.scrollTo(0, 150);
        return;
      }
      el.scrollIntoView();
      window.scrollBy(0, -500);
      scrollBreadrumbIntoView(el);
    } else {
      el.scrollIntoView();
    }
  }
};

const Breadcrumb = (props) => {
  const {
    id,
    breadcrumb,
    imgUrl,
    isActive,
    breadcrumbTheme,
    size,
    onClick,
    override,
    leftBorderWidth,
    leftBorderColor,
    theme,
  } = props;

  const breadCrumdRef = useRef();

  const [showImage, setShowImage] = useState(true);

  const borderRadius = get(theme, 'config.theme.components.progressCard.borderRadius', '9');

  const activeBreadcrumbTheme = get(theme, 'config.theme.global.breadcrumb.active', {});
  const disabledBreadcrumbTheme = get(theme, 'config.theme.global.breadcrumb.disabled', {});
  const showActiveIndicator = get(activeBreadcrumbTheme, 'showActiveIndicator', false);

  const commonProps = {
    breadcrumb,
    onClick,
    isActive,
    imgUrl,
    breadcrumbTheme,
    size,
  };
  const {
    id: breadcrumbId,
    title: breadcrumbTitle,
    state: breadcrumbState,
    clickable: isBreadcrumbClickable,
  } = breadcrumb;
  const { total_sections: totalSections, current_section_number: currentSectionNumber } = get(
    breadcrumb,
    'section_progress',
    0,
  );

  return (
    <CardWrapper name='card-wrapper' id={ `breadcrumb-${id}` } override={ override }>
      <BreadCrumbCard
        tabIndex={ isBreadcrumbClickable || breadcrumb.active || isActive ? '0' : '-1' }
        aria-label={ breadcrumbTitle }
        ref={ breadCrumdRef }
        borderRadius={ borderRadius }
        name='breadcrumb-card'
        size={ size }
        theme={ breadcrumbTheme }
        isActive={ isActive }
        breadcrumb={ breadcrumb }
        activeBreadcrumbTheme={ activeBreadcrumbTheme }
        disabledBreadcrumbTheme={ disabledBreadcrumbTheme }
        { ...commonProps }
        onFocus={ (e) => scrollBreadrumbIntoView(e.target) }
        onClick={ () => {
          if (breadcrumb.clickable) {
            onClick(breadcrumbId);
          }
        } }
        onKeyPress={ (event) => {
          if (breadcrumb.clickable && event.key === 'Enter') {
            onClick(breadcrumbId);
          }
        } }
      >
        {leftBorderColor && leftBorderWidth && (
          <LeftBorder colorCode={ leftBorderColor } width={ leftBorderWidth } />
        )}
        <LeftSection name='left-section' size={ size } breadcrumbState={ breadcrumbState }>
          <LabelWrapper name='label' size={ size }>
            {breadcrumbState === 'active' && showActiveIndicator && (
              <ActiveIndicator activeBreadcrumbTheme={ activeBreadcrumbTheme } />
            )}
            <Title
              id={ `breadcrumb-title-${id}` }
              name='title'
              size={ size }
              isActive={ isActive }
              activeBreadcrumbTheme={ activeBreadcrumbTheme }
              disabledBreadcrumbTheme={ disabledBreadcrumbTheme }
            >
              <TitleWrapper
                name='title-wrapper'
                size={ size }
                isActive={ isActive }
                wordCount={ breadcrumb?.title?.split(' ')?.length }
              >
                {breadcrumb.title}
              </TitleWrapper>
            </Title>
            {size === Size.L && (
              <Footer name='footer'>
                <Line name='line' />
                {showSectionProgressLabel(totalSections, currentSectionNumber)}
              </Footer>
            )}
          </LabelWrapper>
          <ProgressLabel
            name='progress-label'
            isActive={ isActive }
            activeBreadcrumbTheme={ activeBreadcrumbTheme }
            disabledBreadcrumbTheme={ disabledBreadcrumbTheme }
            size={ size }
          >
            {showSectionProgressLabel(totalSections, currentSectionNumber)}
          </ProgressLabel>
        </LeftSection>
        <Image
          showImage={ showImage }
          onError={ (i) => {
            i.target.style.display = 'none';
          } }
          breadCrumbId={ breadcrumb.id }
          borderRadius={ borderRadius }
          name='image'
          src={ imgUrl }
          isActive={ isActive }
          className={ `${process.env.REACT_APP_CARRIER_ID}-breadcrumb` }
          alt=''
          activeBreadcrumbTheme={ activeBreadcrumbTheme }
          disabledBreadcrumbTheme={ disabledBreadcrumbTheme }
        />
        {!isActive && size !== Size.L && size !== Size.B && (
          <DisabledDiv
            borderRadius={ borderRadius }
            name='disabled-div'
            state={ breadcrumbState }
            theme={ disabledBreadcrumbTheme }
          />
        )}
      </BreadCrumbCard>
    </CardWrapper>
  );
};

export default withTheme(Breadcrumb);
